import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, pick } from "@/contexts/LanguageContext";
import SeoHead from "@/components/SeoHead";
import { pathFor, ROUTE_KEYS } from "@/i18n/routeMap";
import { buildLocalBusinessJsonLd, buildBreadcrumb } from "@/lib/seo";

const COPY = {
  en: { title: "Luxury Car Rental Guides", subtitle: "Practical guides for clients renting in Benidorm, Alicante, Costa Blanca, Valencia and Murcia.", read: "Read guide", label: "Guide", empty: "No guides yet." },
  es: { title: "Guías de alquiler de coches de lujo", subtitle: "Guías prácticas para clientes que alquilan en Benidorm, Alicante, Costa Blanca, Valencia y Murcia.", read: "Leer guía", label: "Guía", empty: "Todavía no hay guías." },
  ru: { title: "Гиды по аренде премиальных автомобилей", subtitle: "Практические гиды для клиентов в Бенидорме, Аликанте, на Коста-Бланке, в Валенсии и Мурсии.", read: "Читать гид", label: "Гид", empty: "Гидов пока нет." },
  fr: { title: "Guides de location de voitures de luxe", subtitle: "Des guides pratiques pour louer à Benidorm, Alicante, sur la Costa Blanca, à Valence et à Murcie.", read: "Lire le guide", label: "Guide", empty: "Aucun guide pour le moment." },
  de: { title: "Ratgeber zur Luxusautovermietung", subtitle: "Praktische Ratgeber für Mietkunden in Benidorm, Alicante, an der Costa Blanca, in Valencia und Murcia.", read: "Ratgeber lesen", label: "Ratgeber", empty: "Noch keine Ratgeber vorhanden." },
  it: { title: "Guide al noleggio di auto di lusso", subtitle: "Guide pratiche per chi noleggia a Benidorm, Alicante, sulla Costa Blanca, a Valencia e Murcia.", read: "Leggi la guida", label: "Guida", empty: "Non ci sono ancora guide." },
  nl: { title: "Gidsen voor luxe autoverhuur", subtitle: "Praktische gidsen voor huurders in Benidorm, Alicante, aan de Costa Blanca, in Valencia en Murcia.", read: "Lees de gids", label: "Gids", empty: "Er zijn nog geen gidsen." },
  pl: { title: "Poradniki wynajmu luksusowych samochodów", subtitle: "Praktyczne poradniki dla klientów wynajmujących w Benidorm, Alicante, na Costa Blanca, w Walencji i Murcji.", read: "Czytaj poradnik", label: "Poradnik", empty: "Nie ma jeszcze poradników." },
};

export default function GuidesList() {
  const { lang, t } = useLang();
  const [guides, setGuides] = useState([]);
  const [settings, setSettings] = useState({});
  const c = COPY[lang] || COPY.en;

  useEffect(() => {
    (async () => {
      try {
        const [g, s] = await Promise.all([api.get("/guides"), api.get("/settings")]);
        setGuides(g.data || []);
        setSettings(s.data || {});
      } catch { /* ignore */ }
    })();
  }, []);

  const jsonLd = [
    buildLocalBusinessJsonLd(settings),
    buildBreadcrumb([{ name: c.title, path: pathFor(ROUTE_KEYS.GUIDES, lang) }]),
  ];

  return (
    <div className="bg-[#0C0C0C] min-h-screen" data-testid="guides-list-page">
      <SeoHead title={c.title} description={c.subtitle} lang={lang} pageKey={ROUTE_KEYS.GUIDES} jsonLd={jsonLd} />
      <div className="pt-32 md:pt-40 pb-24 px-6 md:px-10 max-w-6xl mx-auto">
        <nav className="text-[11px] tracking-[0.18em] uppercase text-[#8A8A8A] flex items-center gap-2 mb-4">
          <Link to={pathFor(ROUTE_KEYS.HOME, lang)} className="hover:text-[#C9A55A]">{t.nav.home || "Home"}</Link>
          <ChevronRight size={12} />
          <span className="text-[#C9A55A]">{c.title}</span>
        </nav>
        <h1 className="font-display text-[#F5F1EB] text-4xl md:text-6xl">{c.title}</h1>
        <p className="text-[#F5F1EB]/70 font-light mt-4 max-w-2xl">{c.subtitle}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
          {guides.length === 0 && <p className="text-[#8A8A8A] text-sm">{c.empty}</p>}
          {guides.map((g) => {
            const slug = g.slug?.[lang] || g.slug?.en;
            const title = pick(g.title, lang) || g.title?.en;
            const intro = pick(g.intro, lang);
            return (
              <Link key={g.id} to={`${pathFor(ROUTE_KEYS.GUIDES, lang)}/${slug}`} className="border border-[#2A2A2A] hover:border-[#C9A55A] p-6 group block" data-testid={`guide-card-${slug}`}>
                <div className="text-overline text-[#C9A55A] mb-2">{c.label}</div>
                <h2 className="font-display text-[#F5F1EB] text-xl group-hover:text-[#C9A55A] transition-colors">{title}</h2>
                {intro && <p className="text-[#F5F1EB]/65 text-sm mt-3 font-light line-clamp-3">{intro}</p>}
                <div className="mt-4 text-[11px] tracking-[0.18em] uppercase text-[#C9A55A] inline-flex items-center gap-1">
                  {c.read} <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
